import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getCurrentWarehouseFilter,
  isWarehouseAwareQueryKey,
  queryClient,
  setCurrentWarehouseFilter,
  WAREHOUSE_FILTER_STORAGE_KEY,
} from "./queryClient";
import { useAuth } from "./auth-context";
import { getRoleLevel, type Warehouse } from "@shared/schema";

const STORAGE_KEY = WAREHOUSE_FILTER_STORAGE_KEY;
const ALL_WAREHOUSES = "all" as const;

type WarehouseFilterValue = string | typeof ALL_WAREHOUSES;

interface WarehouseFilterContextType {
  selectedWarehouseId: WarehouseFilterValue;
  setSelectedWarehouseId: (value: WarehouseFilterValue) => void;
  warehouses: Warehouse[];
  isLoading: boolean;
  canSwitch: boolean;
}

const WarehouseFilterContext = createContext<WarehouseFilterContextType | null>(null);

function readPersistedFilter(): WarehouseFilterValue {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && stored.length > 0) return stored as WarehouseFilterValue;
  } catch {
    /* ignore */
  }
  return ALL_WAREHOUSES;
}

function writePersistedFilter(value: WarehouseFilterValue) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

export function WarehouseFilterProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  // canSwitch er FALSK mens /api/auth/me er underveis, rett og slett fordi
  // brukeren ikke er kjent enda -- ikke fordi vedkommende mangler tilgang. De
  // to tilfellene maa ikke behandles likt: se effekten under.
  const canSwitch = !!user?.role && getRoleLevel(user.role) >= getRoleLevel("admin");

  const [selectedWarehouseId, setSelected] = useState<WarehouseFilterValue>(() =>
    canSwitch ? readPersistedFilter() : ALL_WAREHOUSES,
  );

  const { data: warehouses = [], isLoading } = useQuery<Warehouse[]>({
    queryKey: ["/api/warehouses"],
    enabled: isAuthenticated && canSwitch,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    // Ikke roer filteret mens innloggingen fortsatt lastes. Verdien er alt
    // primet synkront fra localStorage ved modullasting nettopp for at den
    // foerste spoerringen skal ha riktig lager -- nulles den ut her, gaar den
    // spoerringen ufiltrert ut og viser ALLE lagre mens velgeren sier
    // «Snarøya». Se kommentaren over currentWarehouseFilter i queryClient.ts.
    if (authLoading) return;
    if (canSwitch) {
      // Restore persisted selection when the user gains admin access
      setSelected(readPersistedFilter());
    } else {
      setSelected(ALL_WAREHOUSES);
      setCurrentWarehouseFilter(null);
    }
  }, [canSwitch, authLoading]);

  // Holder det aktive filteret i sync og kaster gammel data hvis det
  // effektive filteret endret seg etter at spoerringene alt var kjoert
  // (typisk: brukeren viser seg aa ikke ha lov til aa bytte lager).
  useEffect(() => {
    if (authLoading) return;
    const next = canSwitch ? selectedWarehouseId : null;
    const before = getCurrentWarehouseFilter();
    setCurrentWarehouseFilter(next);
    if (getCurrentWarehouseFilter() !== before) {
      // refetchType: "all" ogsaa for spoerringer som er UNDERVEIS. Uten dette
      // ble en invalidering som landet midt i en paagaaende henting deduplisert
      // bort: svaret fra den ufiltrerte spoerringen ble staaende i cachen, og
      // med staleTime paa 60 s hentet ingenting det inn igjen. Da viste
      // pakkesiden Hoenefoss-ordrer under «Snarøya» resten av oekten, helt til
      // noen byttet lager manuelt.
      queryClient.invalidateQueries({
        predicate: (query) => isWarehouseAwareQueryKey(query.queryKey?.[0]),
        refetchType: "all",
      });
    }
  }, [selectedWarehouseId, canSwitch, authLoading]);

  const setSelectedWarehouseId = useCallback(
    (value: WarehouseFilterValue) => {
      setSelected(value);
      if (canSwitch) writePersistedFilter(value);
      setCurrentWarehouseFilter(canSwitch ? value : null);
      queryClient.invalidateQueries({
        predicate: (query) => isWarehouseAwareQueryKey(query.queryKey?.[0]),
      });
    },
    [canSwitch],
  );

  const value = useMemo<WarehouseFilterContextType>(
    () => ({
      selectedWarehouseId,
      setSelectedWarehouseId,
      warehouses,
      isLoading,
      canSwitch,
    }),
    [selectedWarehouseId, setSelectedWarehouseId, warehouses, isLoading, canSwitch],
  );

  return (
    <WarehouseFilterContext.Provider value={value}>{children}</WarehouseFilterContext.Provider>
  );
}

export function useWarehouseFilter(): WarehouseFilterContextType {
  const context = useContext(WarehouseFilterContext);
  if (!context) {
    throw new Error("useWarehouseFilter must be used within a WarehouseFilterProvider");
  }
  return context;
}

export const WAREHOUSE_FILTER_ALL = ALL_WAREHOUSES;
